import React, { useState } from "react";
import {
  CrossWordMatchQuestion,
  CrossWordMatchOption,
  CrossWordQuestion,
  ActivityMeta,
} from "../../types";
import showToast from "@utils/toast";
import { useActivityCoreData } from "src/hooks/useActivityCoreData";
import { newActivity, updateActivity } from "src/service/activities.services";
import ActivityCore from "./activities.core";
import { getAssetUrl, getNextSerialId } from "@utils/id.generator";
import { useNavigate } from "react-router-dom";

interface ICrossWordActivityFormProps {
  lessonMeta?: ActivityMeta;
  initialActivity?: CrossWordMatchQuestion;
  activityId: string;
}

const CrossWordActivityForm = ({
  lessonMeta,
  initialActivity,
  activityId,
}: ICrossWordActivityFormProps) => {
  const isEditing = !!initialActivity;

  const navigate = useNavigate();

  const [isLoading, setLoading] = useState<boolean>(false);
  const [questions, setQuestions] = useState<CrossWordQuestion[]>(
    initialActivity?.data.question ||
      Array.from({ length: 4 }, () => ({ text: "", audioUrl: "" }))
  );
  const [options, setOptions] = useState<CrossWordMatchOption[]>(
    initialActivity?.data.options ||
      Array.from({ length: 4 }, () => ({ id: "", text: "", audioUrl: "" }))
  );
  const [answer, setAnswer] = useState<string[]>(
    initialActivity?.data.answer || ["", "", "", ""]
  );

  const newId = getNextSerialId(options);
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const reorder = <T,>(arr: T[], from: number, to: number): T[] => {
    const copy = [...arr];
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
  };

  const handleDrop = () => {
    if (
      draggedIdx === null ||
      dragOverIdx === null ||
      draggedIdx === dragOverIdx
    )
      return;

    const reordered = reorder(options, draggedIdx, dragOverIdx);
    setOptions(reordered);

    const newAnswer = reordered.map((opt) =>
      answer.includes(opt.id) ? opt.id : ""
    );
    setAnswer(newAnswer);

    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const coreState = useActivityCoreData({
    instruction: initialActivity?.instruction,
    localizedInstruction: initialActivity?.localizedInstruction,
    difficulty: initialActivity?.difficulty,
    tags: initialActivity?.tags,
    isActive: initialActivity?.isActive || false,
    activityNumber: initialActivity?.activityNumber || 0,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();
    const isValidId = isEditing ? initialActivity?.id : activityId;
    if (!isValidId || isValidId === null || isValidId === undefined) {
      return showToast("Activity Id not provided", false);
    }

    // check if answers have thesame ids and throw error
    let emptyAnsArr: string[] = [];
    for (let index = 0; index < answer.length; index++) {
      const element = answer[index];

      if (!emptyAnsArr.includes(element)) {
        emptyAnsArr.push(element);
      } else {
        return showToast(
          "Cross word matching options must be distinct!",
          false
        );
      }
    }

    const questionState = questions.map((q) => {
      if (!q.audioUrl) {
        return { text: q.text } as CrossWordQuestion;
      }
      return { text: q.text, audioUrl: q.audioUrl } as CrossWordQuestion;
    });

    const questionOptionsState = options.map((opt) => {
      if (!opt.audioUrl) {
        return { id: opt.id || newId, text: opt.text } as CrossWordMatchOption;
      }
      return {
        id: opt.id || newId,
        text: opt.text,
        audioUrl: opt.audioUrl,
      } as CrossWordMatchOption;
    });

    const payload: CrossWordMatchQuestion = {
      id: isValidId,
      activityKind: "CrossWordActivity",
      instruction: coreState.coreData.instruction,
      localizedInstruction: coreState.coreData.localizedInstruction,
      activityNumber: coreState.coreData.activityNumber,
      lessonId: initialActivity?.lessonId ?? (lessonMeta?.lesson as string),
      difficulty: coreState.coreData.difficulty,
      tags: coreState.coreData.tags,
      isActive: coreState.coreData.isActive,
      data: {
        question: questionState as [
          CrossWordQuestion,
          CrossWordQuestion,
          CrossWordQuestion,
          CrossWordQuestion
        ],
        options: questionOptionsState as [
          CrossWordMatchOption,
          CrossWordMatchOption,
          CrossWordMatchOption,
          CrossWordMatchOption
        ],
        answer: answer as [string, string, string, string],
      },
    };

    const request = isEditing
      ? updateActivity(payload, isValidId)
      : newActivity(payload);
    const response = await request;
    if (response?.status !== "success") {
      setLoading(false);
      return showToast("There was error saving activity", false);
    } else {
      setLoading(false);
      showToast(
        `${isEditing ? "Activity Updated" : "Activity Created"} successfully`,
        true
      );
      return navigate("/dashboard/levels/lessons/activities");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-4xl mx-auto bg-white p-6 rounded-md shadow space-y-8"
    >
      <h2 className="text-base font-semibold text-gray-800">
        {isEditing ? "Edit" : "Create"} Crossword Match Activity
      </h2>

      <ActivityCore data={coreState} />

      <div className="space-y-6">
        <h3 className="font-medium text-gray-700">Questions & Matches</h3>
        {questions.map((q, idx) => (
          <div
            key={idx}
            className="border border-gray-200 p-4 rounded-md space-y-3"
          >
            <h4 className="text-sm font-semibold text-gray-600">
              Question {idx + 1}
            </h4>
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                className="input-style"
                value={q.text}
                placeholder="Question text"
                required
                onChange={(e) =>
                  setQuestions((prev) => {
                    const updated = [...prev];
                    updated[idx] = { ...updated[idx], text: e.target.value };
                    return updated;
                  })
                }
              />
              <input
                className="input-style"
                min={6}
                maxLength={6}
                value={q.audioUrl}
                placeholder="Audio Code(optional)"
                required={false}
                onChange={(e) =>
                  setQuestions((prev) => {
                    const updated = [...prev];
                    updated[idx] = {
                      ...updated[idx],
                      audioUrl: e.target.value,
                    };
                    return updated;
                  })
                }
              />
            </div>
            {q.audioUrl && (
              <audio controls className="mt-2 w-full">
                <source src={getAssetUrl("audio", q.audioUrl)} />
              </audio>
            )}
            <div>
              <label className="text-xs text-gray-500">
                Select Matching Option
              </label>
              <select
                className="input-style mt-1"
                value={answer[idx]}
                required
                onChange={(e) =>
                  setAnswer((prev) => {
                    const updated = [...prev];
                    updated[idx] = e.target.value;
                    return updated;
                  })
                }
              >
                <option value="">— Choose Option —</option>
                {options.map((o, i) => (
                  <option key={i} value={o.id}>
                    {o.text || `Option ${i + 1}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2 pt-2">
        <h3 className="font-medium text-gray-700">Option List (Reorder)</h3>
        <div
          className="flex overflow-x-auto gap-4 pb-2"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          {options.map((opt, idx) => (
            <div
              key={idx}
              draggable
              onDragStart={() => setDraggedIdx(idx)}
              onDragEnter={() => setDragOverIdx(idx)}
              className={`min-w-[160px] max-w-[160px] flex-shrink-0 p-3 bg-gray-50 rounded-md shadow-md border
              ${draggedIdx === idx ? "opacity-50" : ""}
              ${
                answer.includes(opt.id)
                  ? "border-emerald-500"
                  : "border-gray-200"
              }`}
            >
              <div className="w-full space-y-1">
                <input
                  className="input-style text-xs"
                  required
                  placeholder={`Option ${idx + 1}`}
                  value={opt.text}
                  onChange={(e) => {
                    const updated = [...options];
                    updated[idx] = {
                      ...updated[idx],
                      text: e.target.value,
                      id: updated[idx].id || newId,
                    };
                    setOptions(updated);
                  }}
                />
                <input
                  className="input-style text-xs"
                  placeholder="Audio Code"
                  min={6}
                  maxLength={6}
                  value={opt.audioUrl}
                  onChange={(e) => {
                    const updated = [...options];
                    updated[idx].audioUrl = e.target.value;
                    setOptions(updated);
                  }}
                />
                {/* {opt.audioUrl && ( */}
                <audio controls className="w-full mt-2">
                  <source
                    src={getAssetUrl("audio", `${opt.audioUrl}`)}
                    type="audio/mpeg"
                  />
                  Your browser does not support the audio element.
                </audio>
                {/* )} */}
                <p className="text-[10px] text-center text-gray-400 pt-1">
                  Drag to reorder
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-4">
        <button type="submit" className="button-primary">
          {isEditing ? "Update Activity" : "Save Activity"}
        </button>
      </div>
    </form>
  );
};

export default CrossWordActivityForm;
